import { formatDate } from '@frontend/mwm-ui'

import type {
  ValueFormatterParams,
  GetQuickFilterTextParams,
  ITooltipParams,
  ICellRendererParams,
  ColDef,
  RowClickedEvent,
  ValueGetterParams,
  ValueGetterFunc,
} from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { Props as IDetailedScoreCellRendererProps } from '~/components/CellRenderers/ClusterScoreCellRenderer'
import type { IAddButtonTooltip } from '~/components/Tables/Table'
import type { IAP_STATUS } from '~/types/iap'
import type { IUserPublic } from '~/types/user'

import {
  lastUpdateComparator,
  scoreComparator,
  userFieldsComparator,
  valueComparator,
  usersComparator,
  lastUpdateComparatorV2,
} from './comparators'
import { strDateFilterComparator, dateComparator, dateFilterComparator } from './date'

import ApplicationCellRenderer from '~/components/CellRenderers/ApplicationCellRenderer'
import ClusterScoreCellRenderer from '~/components/CellRenderers/ClusterScoreCellRenderer'
import DateCellRenderer from '~/components/CellRenderers/DateCellRenderer'
import DateISOCellRenderer from '~/components/CellRenderers/DateISOCellRenderer'
import IapStatusCellRenderer from '~/components/CellRenderers/IAPStatusCellRenderer'
import { IdentityCellRenderer } from '~/components/CellRenderers/IdentityCellRenderer'
import LastUpdateCellRenderer, { LastUpdateCellRendererV2 } from '~/components/CellRenderers/LastUpdateCellRenderer'
import PlatformCellRenderer from '~/components/CellRenderers/PlatformCellRenderer'
import RatingCellRenderer from '~/components/CellRenderers/RatingCellRenderer'
import ScoreCellRenderer from '~/components/CellRenderers/ScoreCellRenderer'
import TextAndTranslationCellRenderer from '~/components/CellRenderers/TextAndTranslationCellRenderer'
import UnauthorizedTooltip from '~/components/UnauthorizedTooltip'
import { IAP_STATUS_LABEL_MAPPING } from '~/const'

export const isFocusedCellActions = (event: RowClickedEvent, id = 'actions'): boolean =>
  event.api.getFocusedCell()?.column.getId() === id

export const isFocusedCellInteractive = (
  event: RowClickedEvent,
  id = ['actions', 'files', 'selection_checkbox']
): boolean => id.includes(event.api.getFocusedCell()?.column.getId() || '')

export const getButtonWithTooltip = (tooltipName: IAddButtonTooltip, children: ReactElement): ReactElement => {
  switch (tooltipName) {
    case 'unauthorized':
      return <UnauthorizedTooltip>{children}</UnauthorizedTooltip>
    default:
      return children
  }
}

interface ICustomProperties extends Partial<ColDef> {
  field: string
}

interface ICustomPropertiesWithValueGetter extends Partial<ColDef> {
  valueGetter: string | ValueGetterFunc
}

/**
 * It returns a common definition for the "Actions" column of an AG grid table.
 * @param ActionsCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const actionsColDef = (
  ActionsCellRenderer: ({ context, data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    cellRenderer: ActionsCellRenderer,
    cellStyle: {
      justifyContent: 'end',
      overflow: 'visible',
    },
    headerName: '',
    minWidth: 70,
    pinned: 'right',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 70,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Application" column of an AG grid table.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const applicationColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: ApplicationCellRenderer,
    comparator: ({ name: appNameA }: { name: string }, { name: appNameB }: { name: string }): number => {
      if (!appNameA) return 1
      if (!appNameB) return -1
      return appNameA.toLowerCase().localeCompare(appNameB.toLowerCase())
    },
    filter: 'agTextColumnFilter',
    filterValueGetter: ({ data }) => data?.app_name,
    getQuickFilterText: ({ data }) => data?.name || '',
    headerName: 'Application',
    menuTabs: ['filterMenuTab'],
    minWidth: 80,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    tooltipField: 'app_name',
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Applications" column of an AG grid table.
 * @param ApplicationsListCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const applicationsListColDef = (
  ApplicationsListCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    cellRenderer: ApplicationsListCellRenderer,
    headerName: 'Applications',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 250,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Badge" column of an AG grid table.
 * @param BadgeCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition.
 * @return Column definition.
 */
export const badgeColDef = (
  BadgeCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties | ICustomPropertiesWithValueGetter
): ColDef => {
  return {
    cellRenderer: BadgeCellRenderer,
    filter: 'agSetColumnFilter',
    menuTabs: ['filterMenuTab'],
    minWidth: 120,
    resizable: false,
    sortable: false,
    suppressMovable: true,
    suppressNavigable: true,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Date ISO" column of an AG grid table.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const dateIsoColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: DateISOCellRenderer,
    comparator: strDateFilterComparator,
    filter: 'agDateColumnFilter',
    menuTabs: ['filterMenuTab'],
    minWidth: 120,
    resizable: false,
    suppressSizeToFit: true,
    ...customProperties,
  }
}

/**
 * It returns a common definition for columns of an AG grid table that display a date in the "Mmm dd, yyyy" format.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const dateColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: DateCellRenderer,
    comparator: dateComparator,
    filter: 'agDateColumnFilter',
    filterParams: {
      buttons: ['clear'],
      comparator: dateFilterComparator,
    },
    getQuickFilterText: ({ value }: GetQuickFilterTextParams) => formatDate(value),
    menuTabs: ['filterMenuTab'],
    resizable: false,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 150,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Status" column of an AG grid table. Its cells contain an IapStatusBadge component.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const iapStatusColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: IapStatusCellRenderer,
    filter: 'true',
    headerName: 'Status',
    menuTabs: ['filterMenuTab'],
    suppressSizeToFit: true,
    tooltipValueGetter: (params: ITooltipParams) => params.valueFormatted,
    valueFormatter: (params: ValueFormatterParams) => IAP_STATUS_LABEL_MAPPING[params.value as IAP_STATUS],
    width: 200,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Identity" (basically a UserCard component) column of an AG grid table.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const identityColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: IdentityCellRenderer,
    comparator: usersComparator,
    filter: 'agTextColumnFilter',
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IUserPublic>): string =>
      data ? `${data.name} ${data.profile?.job_title}` : '',
    headerName: 'Identity',
    menuTabs: ['filterMenuTab'],
    minWidth: 220,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Last update" column of an AG grid table.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const lastUpdateColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: LastUpdateCellRenderer,
    comparator: lastUpdateComparator,
    filter: 'agDateColumnFilter',
    filterParams: {
      buttons: ['clear'],
      comparator: dateFilterComparator,
      valueGetter: ({ data }: ValueGetterParams) => {
        if (!data) return undefined
        return data.updated_at_ts
      },
    },
    getQuickFilterText: ({ data }) => (data?.updated_by ? data.updated_by.name : ''),
    headerName: 'Last update by',
    menuTabs: ['filterMenuTab'],
    minWidth: 220,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Last update" column of an AG grid table.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const lastUpdateColDefV2 = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: LastUpdateCellRendererV2,
    comparator: lastUpdateComparatorV2,
    filter: 'agDateColumnFilter',
    filterParams: {
      buttons: ['clear'],
      comparator: dateFilterComparator,
      valueGetter: ({ data }: ValueGetterParams<{ updated_at: number }>) => {
        if (!data) return undefined
        return data.updated_at
      },
    },
    getQuickFilterText: ({ data }) => (data?.updated_by ? data.updated_by.name : ''),
    headerName: 'Last update by',
    menuTabs: ['filterMenuTab'],
    minWidth: 220,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Created by" column of an AG grid table.
 * @param CreatedByTSCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const createdByColDef = (
  CreatedByTSCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    cellRenderer: CreatedByTSCellRenderer,
    comparator: userFieldsComparator,
    filter: 'agTextColumnFilter',
    headerName: 'Creation',
    menuTabs: ['filterMenuTab'],
    minWidth: 230,
    ...customProperties,
  }
}

export const teamColDef = (
  TeamCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    cellRenderer: TeamCellRenderer,
    headerName: 'Team',
    minWidth: 150,
    resizable: false,
    suppressHeaderMenuButton: true,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Long text" column of an AG grid table.
 * @param LongTextCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const longTextColDef = (
  LongTextCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    autoHeight: true,
    cellRenderer: LongTextCellRenderer,
    minWidth: 200,
    sortable: false,
    suppressHeaderMenuButton: true,
    width: 400,
    wrapText: true,
    ...customProperties,
  }
}

/**
 * It returns a common definition for AG grid table columns whose cells display a logo for Google Play, App Store or the web.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const platformColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: PlatformCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['clear'],
    },
    headerName: 'Platform',
    menuTabs: ['filterMenuTab'],
    width: 120,
    ...customProperties,
  }
}

/**
 * It returns a common definition for "Rating" AG grid table columns. Their cells are five stars whose background color is filled depending on the provided value.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const ratingColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    cellRenderer: RatingCellRenderer,
    headerName: 'Rating',
    minWidth: 120,
    resizable: false,
    suppressHeaderMenuButton: true,
    ...customProperties,
  }
}

/**
 * It returns a common definition for AG grid table columns whose cells are composed of a score out of 100 above a progress bar. Their color depends on the score.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const scoreColDef = (customProperties: ICustomProperties | ICustomPropertiesWithValueGetter): ColDef => {
  return {
    cellRenderer: ScoreCellRenderer,
    comparator: scoreComparator,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 170,
    ...customProperties,
  }
}

/**
 * It returns a common definition for AG grid table columns whose cells are composed of a text and the mention "View original version" underneath. When the latter is hovered, a tooltip containing a translation appears.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const textAndTranslationColDef = (customProperties: ICustomProperties): ColDef => {
  return {
    autoHeight: true,
    cellRenderer: TextAndTranslationCellRenderer,
    cellStyle: { padding: '1rem 0' },
    sortable: false,
    suppressHeaderMenuButton: true,
    ...customProperties,
  }
}

/**
 * It returns a common definition for the "Text" column of an AG grid table.
 * @param TextCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const textColDef = (
  TextCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  const { field, headerName, tooltipValueGetter, valueGetter } = customProperties

  return {
    cellRenderer: TextCellRenderer,
    comparator: valueComparator,
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      trimInput: true,
    },
    headerTooltip: headerName,
    menuTabs: ['filterMenuTab'],
    minWidth: 120,
    tooltipField: !tooltipValueGetter && !valueGetter ? field : undefined,
    ...customProperties,
  }
}

export const threeFlagColDef = (
  ThreeFlagCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    cellRenderer: ThreeFlagCellRenderer,
    menuTabs: ['filterMenuTab'],
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
    width: 180,
    ...customProperties,
  }
}

/** For cells that look like "[main score]% ⓘ". On icon hover, a tooltip with the score breakdown appears with list items as follows: "[Label for a kind of score]: [score]%" */
export const detailedScoreColDef = (customProperties: ICustomPropertiesWithValueGetter): ColDef => {
  return {
    cellRenderer: ClusterScoreCellRenderer,
    comparator: (a: IDetailedScoreCellRendererProps, b: IDetailedScoreCellRendererProps): number => {
      const valueA = a.cellScore.value
      const valueB = b.cellScore.value

      if (valueA === null && valueB === null) return 0
      if (valueA === null) return -1
      if (valueB === null) return 1

      return valueA - valueB
    },
    ...customProperties,
  }
}

/**
 * It returns a common definition for columns with a main text and a subtext.
 * @param WithSubTextCellRenderer The cell renderer.
 * @param customProperties An object that extends or overwrites the common definition. A "field" (key from the row data object used for this column) is mandatory.
 * @return Column definition.
 */
export const withSubTextColDef = (
  WithSubTextCellRenderer: ({ data }: ICellRendererParams) => ReactElement,
  customProperties: ICustomProperties
): ColDef => {
  return {
    cellRenderer: WithSubTextCellRenderer,
    minWidth: 200,
    width: 450,
    ...customProperties,
  }
}
